import React from "react";
import { css, Theme, useTheme } from "@emotion/react";

import Image from "core/Components/Image";
import mixins from "theme/mixins";
import { isEmpty } from "lodash";

type Props = {
    data: BannerWithExtendedProfile,
    className?: string,
};

const ProfileSection: React.FC<Props> = ({ data, className = "" }) => {
    const theme: Theme = useTheme();

    const style = css`
        display: grid;
        grid-template-columns: 50% 50%;
        grid-template-rows: 1fr;
        padding: 0 130px;
        min-height: 290px;
        background-color: ${data.extendedBackgroundColour};

        ${data.enableNoiseEffectExtendedBackground && css`
            ${mixins.noiseBackground}
        `}

        ${theme.breakpoints.down("lg")} {
            grid-template-columns: 1fr 1fr;
            padding: 50px 0 0;
            grid-template-rows: auto auto;
            height: auto;
        }

        ${theme.breakpoints.down("xs")} {
            padding: 30px 0 0;
        }
    `;

    const badgeContainerStyle = css`
        grid-column: 1;
        grid-row: 1;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 150px;

        img {
            height: 80px !important;
            width: auto !important;
        }

        ${theme.breakpoints.only("xxl")} {
            padding: 20px 100px;
        }

        ${theme.breakpoints.only("xl")} {
            padding: 0 60px;
        }

        ${theme.breakpoints.down("lg")} {
            grid-column: 1 / -1;
            grid-row: 2;
            padding: 0 150px;
            min-height: 110px;
            background-color: ${data.extendedBackgroundColour};

            ${!data.badges?.length && css`
                display: none;
            `}

            img {
                height: 70px !important;
                width: auto !important;
            }
        }

        ${theme.breakpoints.down("sm")} {
            padding: 0 100px;
            
            img {
                height: 50px !important;
                width: auto !important;
            }
        }

        ${theme.breakpoints.down("xs")} {
            padding: 0 70px;

            img {
                height: 40px !important;
                width: auto !important;
            }
        }
    `;

    const authorContainerStyle = css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 20px 0;
        font-size: 23px;
        
        p {
            font-family: ${theme.fonts.figtree} !important;
        }

        ${theme.breakpoints.down("lg")} {
            grid-column: 1;
            grid-row: 1;
            padding: 0 0 0 100px;
            min-height: 220px;
        }

        ${theme.breakpoints.down("md")} {
            padding: 0 0 0 60px;
            font-size: 20px;
        }

        ${theme.breakpoints.down("xs")} {
            font-size: 16px;
            padding: 0 0 0 28px;
            line-height: 21px;
        }
    `;

    const bookAuthorStyle = css`
        color: ${data.authorColour};
    `;

    const authorBioStyle = css`
        white-space: pre-line;
        line-height: 30px;
        color: ${data.authorBiosColour};
        
        ${theme.breakpoints.down("xs")} {
            font-size: 16.18px;
            line-height: 21.11px;
        }
    `;

    const profileWrapperStyle = css`
        position: absolute;
        left: 65%;
        top: 900px;
        transform: translateY(-65%);
        width: 434px;
        height: 434px;
        z-index: 10;

        ${theme.breakpoints.only("3xl")} {
            left: 68%;
            top: 780px;
            width: 380px;
            height: 380px;
        }

        ${theme.breakpoints.only("xxl")} {
            left: 70%;
            top: 650px;
            width: 300px;
            height: 300px;
        }

        ${theme.breakpoints.only("xl")} {
            left: 72%;
            top: 556px;
            width: 250px;
            height: 250px;
        }

        ${theme.breakpoints.down("lg")} {
            position: relative;
            left: unset;
            right: 100px;
            top: unset;
            justify-self: end;
            align-self: center;
            transform: none;
            width: 170px;
            height: 170px;
            z-index: 1;
            grid-column: 2;
            grid-row: 1;
        }

        ${theme.breakpoints.down("sm")} {
            right: 60px;
        }

        ${theme.breakpoints.down("xs")} {
            right: 30px;
            width: 143px;
            height: 143px;
        }
    `;

    const profileCircleBgStyle = css`
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background-color: ${data.authorPhotoBackgroundColour};
    `;

    const profileImageStyle = css`
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;

        img {
            width: 100% !important;
            height: auto !important;
            min-width: unset !important;
            min-height: unset !important;
            display: block;
        }
    `;

    return (
        <div css={style} className={className}>
            {/* Badges */}
            <div css={badgeContainerStyle}>
                {data.badges?.map((badge: Badge, index) => (
                    <Image key={`badge-${index}`} image={badge.badge} />
                ))}
            </div>
            {/* Author Bios */}
            <div css={authorContainerStyle}>
                <p css={bookAuthorStyle}>{data.bookAuthor}</p>
                <p css={authorBioStyle}>{data.authorBios}</p>
            </div>
            {!isEmpty(data.authorPhoto) && (
                <div css={profileWrapperStyle}>
                    <div css={profileCircleBgStyle} />
                    <div css={profileImageStyle}>
                        <Image image={data.authorPhoto} useFocusPoint={data.useFocusPointAuthor} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileSection;
